/**
 * Build-time constants, injected by webpack's DefinePlugin.
 *
 * Each value is read through `fromEnv` rather than referenced directly.
 * DefinePlugin substitutes the literal `process.env.X` expressions below at
 * build time, but there is no `process` global in the browser, so any key
 * missing from that list becomes a ReferenceError the moment this module is
 * imported — before Office.onReady has run and before any error handling
 * exists. The whole task pane then renders as a bare header with no
 * explanation. Reading through a try/catch turns that into a fallback.
 *
 * (That is not hypothetical: adding RIBBON_ID_SUFFIX to the source while a
 * webpack-dev-server from before the config change was still running produced
 * exactly this blank pane. webpack-dev-server does not re-read
 * webpack.config.js — restart it after touching DefinePlugin.)
 */
function fromEnv(read: () => string | undefined, fallback: string): string {
  try {
    return read() || fallback;
  } catch {
    console.warn('Build-time constant was not substituted; using fallback.');
    return fallback;
  }
}

/**
 * The Excel function namespace this build registers (BIFROST in prod,
 * BIFROST_TEST in test, BIFROST_DEV in dev). Injected at build time by
 * webpack's DefinePlugin from build-envs.js so that display/copy strings in
 * the taskpane and formula builder match the namespace Excel actually
 * registered from the manifest's BifrostNamespace.
 */
export const ADDIN_NAMESPACE = fromEnv(() => process.env.ADDIN_NAMESPACE, 'BIFROST');

/**
 * Suffix on the manifest's ribbon element IDs ('' in prod, '_TEST', '_DEV').
 * Office.ribbon.requestUpdate addresses tabs and groups by the exact IDs in
 * the manifest, so enabling or disabling a control needs this to match.
 *
 * Empty is the prod value, so it can't be distinguished from "missing" — that
 * is fine, since a wrong suffix only means the ribbon keeps its current
 * enabled state rather than anything breaking.
 */
export const RIBBON_ID_SUFFIX = fromEnv(() => process.env.RIBBON_ID_SUFFIX, '');

/** Docs site for this environment — matches the manifest's GetStarted link. */
export const HELP_URL = fromEnv(() => process.env.HELP_URL, 'https://app.bifrostledger.com/docs');

/** Web portal for this environment. Reached from the ribbon by admins only. */
export const PORTAL_URL = fromEnv(() => process.env.PORTAL_URL, 'https://app.bifrostledger.com');
